import { useEffect, useState } from "react";
import {
  useAlertStore,
  useGeneralStore,
  usePopupStore,
  useThemeStore,
} from "../../../stores";
import { Column, Row } from "../../containers";
import { CashUpdaterFormProps } from "./types";
import { PopupBack, PopupClose } from "../../../popup";
import { Heading } from "../../typography";
import { cashTypeToString } from "../../../utils";
import { FileInput, NumberInput, SelectorInput, TextInput } from "../../inputs";
import { Cash, CashUpdater, Customer } from "../../../types";
import { AlignItems, JustifyContent } from "../../containers/types";
import { Button } from "../../buttons";
import { CashApi } from "../../../api";

const CashUpdaterForm = <T extends {} & { cash: Cash }>(
  props: CashUpdaterFormProps<T>
) => {
  const { cash, back, updateCb } = props;

  const theme = useThemeStore((s) => s.theme);
  const customers = useGeneralStore((s) => s.customers);
  const getCustomerById = useGeneralStore((s) => s.getCustomerById);
  const setPopupBody = usePopupStore((s) => s.setPopupBody);
  const setPopupProperties = usePopupStore((s) => s.setPopupProperties);
  const pushAlert = useAlertStore((s) => s.pushAlert);

  useEffect(() => {
    setPopupProperties({
      style: {
        maxHeight: "80%",
        overflowY: "auto",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const [cashUpdater, setCashUpdater] = useState<CashUpdater>({
    customer: null,
    description: "",
    total_price: cash.total_price,
    image: null,
  });

  const [loading, setLoading] = useState<boolean>(false);

  const setUpdater = <T extends keyof CashUpdater>(
    key: T,
    value: CashUpdater[T]
  ): void => {
    setCashUpdater((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (cash.customer)
      setUpdater("customer", getCustomerById(cash.customer.$oid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cash.customer]);

  const onSubmit = (): void => {
    setLoading(true);
    CashApi.update(cash._id.$oid, cashUpdater)
      .then((res) => {
        setPopupBody(
          back ? <back.component {...back.props} cash={res} /> : null
        );
        if (updateCb) updateCb();
        pushAlert("عملیات موفقیت آمیز");
      })
      .catch((err) => {
        pushAlert(err.msg || "unknown error", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Column style={{ padding: "1rem", position: "relative" }} gap={20}>
      <PopupClose />

      {back ? (
        <PopupBack Component={back.component} props={back.props} />
      ) : (
        <></>
      )}

      <Heading color={theme.textOnOverlay} size={18}>
        {`ویرایش نقدی ${cashTypeToString(cash.type)}`}
      </Heading>

      <Row style={{ width: "100%" }} gap={20} className="factor-activity-row">
        <SelectorInput<Customer>
          items={customers}
          uniqueKey={"fullname"}
          selected={cashUpdater.customer}
          setSelected={(v) => setUpdater("customer", v as Customer | null)}
          stringField={"fullname"}
          renderStrings={["fullname", "phone"]}
          compareFn={(c, v) =>
            c.fullname.toLowerCase().includes(v.toLowerCase()) ||
            c.phone.includes(v)
          }
          label="نام مشتری"
        />

        <TextInput
          label="بابت"
          value={cashUpdater.description}
          setValue={(v) => setUpdater("description", v)}
          inputProps={{ placeholder: cash.description }}
        />

        <NumberInput
          label="مبلغ کل"
          value={cashUpdater.total_price}
          setValue={(v) => setUpdater("total_price", v as number)}
          price
        />
      </Row>

      <Row
        style={{ width: "100%" }}
        gap={20}
        className="factor-activity-row"
        alignItems={AlignItems.End}
      >
        <FileInput
          label="عکس"
          value={cashUpdater.image}
          setValue={(v) => setUpdater("image", v)}
        />

        <Row
          style={{ width: "100%", height: 41 }}
          justifyContent={JustifyContent.End}
        >
          <Button
            loading={loading}
            onClick={onSubmit}
            background={theme.primary}
            color={theme.textOnprimary}
          >
            ثبت
          </Button>
        </Row>
      </Row>
    </Column>
  );
};

export default CashUpdaterForm;
